import { useState, useEffect, useRef } from 'react';
import { Search, Shuffle, Filter, ChevronDown, ChevronUp, ChevronRight, ArrowLeft, Bot } from 'lucide-react';
import nhost from '../nhost';
import { pushBack, popBack } from '../historyStack';

const GRAPE_TYPE = {
  'Röd':  { dot: '#8B2A3E', bg: '#F5E8EB', fg: '#5C1A28' },
  'Vit':  { dot: '#A07830', bg: '#F7F0E6', fg: '#7D5A22' },
  'Rosé': { dot: '#C4566A', bg: '#FCE8EF', fg: '#A03060' },
};

function TypeChip({ type }) {
  const t = GRAPE_TYPE[type] || { dot: '#7A6068', bg: '#F0EBEC', fg: '#7A6068' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: t.bg, color: t.fg, borderRadius: 20, padding: '3px 9px', fontSize: 11, fontWeight: 600 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.dot, flexShrink: 0 }} />
      {type}
    </span>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid rgba(60,20,30,.07)' }}>
      <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.55 }}>{value}</div>
    </div>
  );
}

const cardStyle = {
  background: '#fff',
  border: '1px solid rgba(60,20,30,.08)',
  borderRadius: 16,
  padding: 16,
  marginBottom: 12,
};

const sectionLabel = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 8,
  marginTop: 4,
};

function GrapeDetail({ grape, onClose }) {
  const synonymStr = Array.isArray(grape.synonyms) ? grape.synonyms.join(', ') : grape.synonyms;
  const regionsStr = Array.isArray(grape.regions) ? grape.regions.join(' · ') : grape.regions;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--cream)', zIndex: 200, overflowY: 'auto' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', background: 'var(--cream)', position: 'sticky', top: 0, zIndex: 10,
          borderBottom: '1px solid rgba(60,20,30,.06)',
        }}>
          <button
            onClick={onClose}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wine)', fontSize: 14, fontWeight: 500, padding: '6px 0' }}
          >
            <ArrowLeft size={18} strokeWidth={1.8} />
            Tillbaka
          </button>
          {grape.fromAI && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)', fontStyle: 'italic' }}>
              <Bot size={13} strokeWidth={1.7} />
              Vinhjälparen
            </span>
          )}
        </div>

        <div style={{ padding: '12px 16px 100px' }}>
          {/* Hero */}
          <div style={{ ...cardStyle, textAlign: 'center', paddingTop: 24, paddingBottom: 22, borderRadius: 20 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 600, color: 'var(--wine)', marginBottom: 10, lineHeight: 1.1 }}>
              {grape.name}
            </div>
            <TypeChip type={grape.type} />
            {grape.origin_country && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
                {grape.origin_region ? `${grape.origin_region}, ` : ''}{grape.origin_country}
              </div>
            )}
          </div>

          {/* Grundinfo */}
          <div style={sectionLabel}>Grundinfo</div>
          <div style={{ ...cardStyle, padding: '0 16px' }}>
            <InfoRow label="Synonymer" value={synonymStr} />
            <InfoRow label="Ursprungsland" value={grape.origin_country} />
            <InfoRow label="Ursprungsregion" value={grape.origin_region} />
            <InfoRow label="Typ" value={grape.type} />
          </div>

          {/* Odling & geografi */}
          {(grape.climate || regionsStr) && (
            <>
              <div style={sectionLabel}>Odling & geografi</div>
              <div style={{ ...cardStyle, padding: '0 16px' }}>
                <InfoRow label="Klimat & uttryck" value={grape.climate} />
                <InfoRow label="Viktiga regioner" value={regionsStr} />
              </div>
            </>
          )}

          {/* Smakprofil */}
          {(grape.flavor_profile || grape.tannins || grape.acidity || grape.body) && (
            <>
              <div style={sectionLabel}>Smakprofil</div>
              <div style={{ ...cardStyle, padding: '0 16px' }}>
                <InfoRow label="Smaker & dofter" value={grape.flavor_profile} />
                <InfoRow label="Tanniner" value={grape.tannins} />
                <InfoRow label="Syra" value={grape.acidity} />
                <InfoRow label="Kropp" value={grape.body} />
              </div>
            </>
          )}

          {/* Vinifiering */}
          {grape.aging_potential && (
            <>
              <div style={sectionLabel}>Vinifiering</div>
              <div style={{ ...cardStyle, padding: '0 16px' }}>
                <InfoRow label="Åldringspotential" value={grape.aging_potential} />
              </div>
            </>
          )}

          {/* Mat & servering */}
          {(grape.food_pairings || grape.serving_temp) && (
            <>
              <div style={sectionLabel}>Mat & servering</div>
              <div style={{ ...cardStyle, padding: '0 16px' }}>
                <InfoRow label="Matparningar" value={grape.food_pairings} />
                <InfoRow label="Serveringstemperatur" value={grape.serving_temp} />
              </div>
            </>
          )}

          {/* Kul fakta */}
          {grape.fun_facts && (
            <>
              <div style={sectionLabel}>Kul fakta</div>
              <div style={{ ...cardStyle, fontStyle: 'italic', fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--ink)', lineHeight: 1.6 }}>
                {grape.fun_facts}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function getTempCategory(tempStr) {
  if (!tempStr) return null;
  const match = tempStr.match(/\d+/);
  if (!match) return null;
  const t = parseInt(match[0]);
  if (t <= 12) return 'Kylt';
  if (t <= 15) return 'Svalt';
  return 'Varmt';
}

const FOOD_KEYWORDS = {
  'Fisk': ['fisk', 'skaldjur', 'lax', 'ostron', 'musslor', 'hummer', 'räkor', 'tonfisk', 'torsk', 'bläckfisk', 'seafood'],
  'Kött': ['kött', 'nöt', 'lamm', 'vilt', 'stek', 'fläsk', 'kyckling', 'anka', 'biff', 'entrecote', 'vildsvin', 'cassoulet'],
  'Ost': ['ost'],
  'Pasta': ['pasta', 'pizza', 'risotto'],
  'Dessert': ['dessert', 'choklad', 'glass', 'sötviner', 'kaka', 'frukt', 'dadlar', 'russin'],
};

function matchesFood(foodPairings, category) {
  if (!foodPairings) return false;
  const fp = foodPairings.toLowerCase();
  return FOOD_KEYWORDS[category].some(kw => fp.includes(kw));
}

function grapeCountries(g) {
  const regionCountries = Array.isArray(g.regions)
    ? g.regions.map(r => r.split(',').map(s => s.trim()).at(-1)).filter(Boolean)
    : [];
  return [...new Set([g.origin_country, ...regionCountries].filter(Boolean))];
}

export default function Grapes({ initialOpenGrapeName = null }) {
  const [grapes, setGrapes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('Alla');
  const [countryFilter, setCountryFilter] = useState('Alla');
  const [bodyFilter, setBodyFilter] = useState('Alla');
  const [tanninFilter, setTanninFilter] = useState('Alla');
  const [acidityFilter, setAcidityFilter] = useState('Alla');
  const [tempFilter, setTempFilter] = useState('Alla');
  const [foodFilter, setFoodFilter] = useState('Alla');
  const [showFilters, setShowFilters] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const openedGrapeNameRef = useRef(null);

  useEffect(() => {
    if (!initialOpenGrapeName || !grapes.length) return;
    if (openedGrapeNameRef.current === initialOpenGrapeName) return;
    const grape = grapes.find(g => g.name === initialOpenGrapeName);
    if (grape) {
      openedGrapeNameRef.current = initialOpenGrapeName;
      openGrapeDetail(grape);
    }
  }, [initialOpenGrapeName, grapes]);

  useEffect(() => { fetchGrapes(); }, []);

  async function fetchGrapes() {
    try {
      const { data, error: gqlErr } = await nhost.graphql.request(`
        query {
          grapes(order_by: { name: asc }) {
            id name synonyms type origin_country origin_region flavor_profile serving_temp food_pairings body tannins acidity regions
          }
        }
      `);
      if (gqlErr) throw new Error(gqlErr[0]?.message || 'GraphQL-fel');
      setGrapes(data?.grapes || []);
    } catch (e) {
      setError('Kunde inte ladda druvbiblioteket. Är tabellen skapad i Hasura?');
    }
    setLoading(false);
  }

  async function openGrapeDetail(grape) {
    setDetailLoading(true);
    try {
      const { data } = await nhost.graphql.request(`
        query($id: uuid!) {
          grapes_by_pk(id: $id) {
            id name synonyms type origin_country origin_region
            climate regions flavor_profile tannins acidity body
            aging_potential food_pairings serving_temp fun_facts
          }
        }
      `, { id: grape.id });
      if (data?.grapes_by_pk) {
        pushBack(() => setDetail(null));
        setDetail({ ...data.grapes_by_pk, fromDB: true });
      }
    } catch {}
    setDetailLoading(false);
  }

  const countries = [...new Set(grapes.flatMap(grapeCountries))].sort();

  const filtered = grapes.filter(g => {
    if (typeFilter !== 'Alla' && g.type !== typeFilter) return false;
    if (countryFilter !== 'Alla' && !grapeCountries(g).includes(countryFilter)) return false;
    if (bodyFilter !== 'Alla' && !g.body?.toLowerCase().includes(bodyFilter.toLowerCase())) return false;
    if (tanninFilter !== 'Alla' && !g.tannins?.toLowerCase().includes(tanninFilter.toLowerCase())) return false;
    if (acidityFilter !== 'Alla' && !g.acidity?.toLowerCase().includes(acidityFilter.toLowerCase())) return false;
    if (tempFilter !== 'Alla' && getTempCategory(g.serving_temp) !== tempFilter) return false;
    if (foodFilter !== 'Alla' && !matchesFood(g.food_pairings, foodFilter)) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    if (g.name.toLowerCase().includes(q)) return true;
    if (Array.isArray(g.synonyms) && g.synonyms.some(s => s.toLowerCase().includes(q))) return true;
    if (g.origin_country?.toLowerCase().includes(q)) return true;
    if (g.origin_region?.toLowerCase().includes(q)) return true;
    if (g.flavor_profile?.toLowerCase().includes(q)) return true;
    return false;
  });

  const activeCount = [typeFilter, countryFilter, bodyFilter, tanninFilter, acidityFilter, tempFilter, foodFilter].filter(f => f !== 'Alla').length;
  const searchActive = search.trim().length > 1;
  const noResults = searchActive && filtered.length === 0;

  if (detail) {
    return <GrapeDetail grape={detail} onClose={() => { popBack(); setDetail(null); }} />;
  }

  return (
    <div className="screen" style={{ paddingTop: 8, paddingBottom: 120 }}>

      {/* Sök */}
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <Search size={16} color="var(--muted)" strokeWidth={1.8} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        <input
          type="search"
          placeholder="Sök druva eller synonym..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: '100%', padding: '10px 16px 10px 40px',
            border: '1px solid rgba(60,20,30,.12)', borderRadius: 24,
            fontSize: 14, background: 'white', outline: 'none', fontFamily: 'inherit',
            color: 'var(--ink)', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Slumpa */}
      {grapes.length > 0 && (
        <button
          onClick={() => {
            const pool = filtered.length > 0 ? filtered : grapes;
            openGrapeDetail(pool[Math.floor(Math.random() * pool.length)]);
          }}
          style={{
            width: '100%', marginBottom: 10,
            background: 'var(--wine-light)', border: 'none',
            borderRadius: 12, padding: '10px 16px',
            fontSize: 14, color: 'var(--wine)', fontWeight: 500,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <Shuffle size={16} strokeWidth={1.8} />
          Slumpa en druva
          {filtered.length !== grapes.length && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>
              bland {filtered.length} druvor
            </span>
          )}
        </button>
      )}

      {/* Filter */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setShowFilters(v => !v)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 6,
              background: activeCount > 0 ? 'var(--wine-light)' : 'white',
              border: `1px solid ${activeCount > 0 ? 'var(--wine)' : 'rgba(60,20,30,.12)'}`,
              borderRadius: 12, padding: '9px 14px',
              fontSize: 14, color: activeCount > 0 ? 'var(--wine)' : 'var(--muted)',
              fontWeight: activeCount > 0 ? 500 : 400, cursor: 'pointer',
            }}
          >
            <Filter size={15} strokeWidth={1.8} />
            Filter{activeCount > 0 ? ` (${activeCount})` : ''}
            {showFilters
              ? <ChevronUp size={14} strokeWidth={1.8} style={{ marginLeft: 'auto' }} />
              : <ChevronDown size={14} strokeWidth={1.8} style={{ marginLeft: 'auto' }} />
            }
          </button>
          {activeCount > 0 && (
            <button
              onClick={() => { setTypeFilter('Alla'); setCountryFilter('Alla'); setBodyFilter('Alla'); setTanninFilter('Alla'); setAcidityFilter('Alla'); setTempFilter('Alla'); setFoodFilter('Alla'); }}
              style={{ background: 'none', border: '1px solid rgba(60,20,30,.12)', borderRadius: 12, padding: '9px 12px', fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}
            >
              Rensa
            </button>
          )}
        </div>

        {showFilters && (
          <div style={{ background: 'white', border: '1px solid rgba(60,20,30,.08)', borderRadius: 14, padding: '14px', marginTop: 8 }}>
            {[
              { label: 'Typ', items: ['Alla', 'Röd', 'Vit', 'Rosé'], val: typeFilter, set: setTypeFilter },
              { label: 'Kropp', items: ['Alla', 'Lätt', 'Medel', 'Fylligt'], val: bodyFilter, set: setBodyFilter },
              { label: 'Tanniner', items: ['Alla', 'Låg', 'Medel', 'Hög'], val: tanninFilter, set: setTanninFilter },
              { label: 'Syra', items: ['Alla', 'Låg', 'Medel', 'Hög'], val: acidityFilter, set: setAcidityFilter },
            ].map(({ label, items, val, set }) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 7 }}>{label}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {items.map(item => (
                    <button key={item} onClick={() => set(item)} style={{ border: val === item ? 'none' : '1px solid rgba(60,20,30,.15)', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', background: val === item ? 'var(--wine)' : '#fff', color: val === item ? '#fff' : 'var(--ink)' }}>
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 7 }}>Serveringstemperatur</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { label: 'Alla', value: 'Alla' },
                  { label: 'Kylt ≤12°', value: 'Kylt' },
                  { label: 'Svalt 13–15°', value: 'Svalt' },
                  { label: 'Varmt 16°+', value: 'Varmt' },
                ].map(({ label, value }) => (
                  <button key={value} onClick={() => setTempFilter(value)} style={{ border: tempFilter === value ? 'none' : '1px solid rgba(60,20,30,.15)', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', background: tempFilter === value ? 'var(--wine)' : '#fff', color: tempFilter === value ? '#fff' : 'var(--ink)' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 7 }}>Matparning</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['Alla', 'Fisk', 'Kött', 'Ost', 'Pasta', 'Dessert'].map(v => (
                  <button key={v} onClick={() => setFoodFilter(v)} style={{ border: foodFilter === v ? 'none' : '1px solid rgba(60,20,30,.15)', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', background: foodFilter === v ? 'var(--wine)' : '#fff', color: foodFilter === v ? '#fff' : 'var(--ink)' }}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 7 }}>Ursprungsland</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['Alla', ...countries].map(c => (
                  <button key={c} onClick={() => setCountryFilter(c)} style={{ border: countryFilter === c ? 'none' : '1px solid rgba(60,20,30,.15)', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', background: countryFilter === c ? 'var(--wine)' : '#fff', color: countryFilter === c ? '#fff' : 'var(--ink)' }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: '#fcebeb', color: '#a32d2d', padding: '12px 16px', borderRadius: 12, fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {noResults && (
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            Ingen druva hittades för <strong>"{search.trim()}"</strong>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: 13 }}>Laddar druvbiblioteket...</div>
      )}

      {!loading && !error && (
        <div style={{ background: '#fff', border: '1px solid rgba(60,20,30,.08)', borderRadius: 16, overflow: 'hidden', marginBottom: 8 }}>
          {filtered.length === 0 && !noResults ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
              Inga druvor matchar filtret
            </div>
          ) : filtered.map((g, i) => (
            <div
              key={g.id}
              onClick={() => !detailLoading && openGrapeDetail(g)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 16px', cursor: 'pointer',
                borderTop: i > 0 ? '1px solid rgba(60,20,30,.07)' : 'none',
                background: 'white',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{g.name}</div>
                {Array.isArray(g.synonyms) && g.synonyms.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {g.synonyms.slice(0, 3).join(' · ')}
                  </div>
                )}
                {g.flavor_profile && (
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {g.flavor_profile}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
                <TypeChip type={g.type} />
                {g.origin_country && (
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{g.origin_country}</div>
                )}
              </div>
              <ChevronRight size={16} color="var(--muted)" strokeWidth={1.7} style={{ flexShrink: 0 }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
